import { useState } from 'react';

import { FlaskConical, Loader2, ChevronDown, ChevronRight, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useTestCustomTool } from '@/hooks/useTools';

import type { TestCustomToolDto, TestCustomToolResponse } from '@repo/shared-types';

interface TestToolDialogProps {
  getToolConfig: () => TestCustomToolDto;
  onFieldSelect?: (fieldName: string) => void;
  onSampleFieldsChange?: (fields: string[]) => void;
  onTopLevelKeysDetected?: (keys: string[]) => void;
}

export const TestToolDialog = ({
  getToolConfig,
  onFieldSelect,
  onSampleFieldsChange,
  onTopLevelKeysDetected,
}: TestToolDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState<TestCustomToolResponse | null>(null);
  const [showRawResponse, setShowRawResponse] = useState(false);
  const { mutate: testTool, isPending } = useTestCustomTool();

  const handleTest = () => {
    const config = getToolConfig();
    testTool(config, {
      onSuccess: (data) => {
        setResult(data);
        setIsOpen(true);
        if (data.sample_fields.length > 0) {
          onSampleFieldsChange?.(data.sample_fields);
        }
        if (data.top_level_keys && data.top_level_keys.length > 0) {
          onTopLevelKeysDetected?.(data.top_level_keys);
        }
      },
      onError: (error) => {
        setResult({
          success: false,
          raw_response: null,
          extracted_array: null,
          sample_fields: [],
          error: error instanceof Error ? error.message : 'Request failed',
        });
        setIsOpen(true);
      },
    });
  };

  const handleAddAsFilter = (fieldName: string) => {
    onFieldSelect?.(fieldName);
  };

  if (!isOpen) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={handleTest} disabled={isPending}>
        {isPending ? (
          <Loader2 size={16} className="mr-1 animate-spin" />
        ) : (
          <FlaskConical size={16} className="mr-1" />
        )}
        {isPending ? 'Testing...' : 'Test Tool'}
      </Button>
    );
  }

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={handleTest} disabled={isPending}>
        {isPending ? (
          <Loader2 size={16} className="mr-1 animate-spin" />
        ) : (
          <FlaskConical size={16} className="mr-1" />
        )}
        {isPending ? 'Testing...' : 'Test Tool'}
      </Button>

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-background mx-4 max-h-[85vh] w-full max-w-4xl overflow-auto rounded-lg shadow-xl">
          <div className="space-y-4 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                Test Results
                {result?.success ? (
                  <span className="ml-2 text-sm font-normal text-green-600">Success</span>
                ) : (
                  <span className="ml-2 text-sm font-normal text-red-600">Failed</span>
                )}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                ✕
              </Button>
            </div>

            {result?.error && (
              <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                {result.error}
              </div>
            )}

            {result?.success &&
              !result.extracted_array?.length &&
              result.top_level_keys &&
              result.top_level_keys.length > 0 && (
                <div className="rounded-md border border-yellow-200 bg-yellow-50/50 p-3 text-sm dark:border-yellow-800 dark:bg-yellow-950/30">
                  <span className="font-medium">No array extracted.</span> Available top-level keys:{' '}
                  {result.top_level_keys.map((key) => (
                    <code
                      key={key}
                      className="bg-muted mx-0.5 rounded px-1.5 py-0.5 text-xs font-medium"
                    >
                      {key}
                    </code>
                  ))}
                  <br />
                  <span className="text-muted-foreground text-xs">
                    Set the output path to one of these keys (e.g. &quot;vehicles&quot; or
                    &quot;data.items&quot;)
                  </span>
                </div>
              )}

            {result?.sample_fields && result.sample_fields.length > 0 && (
              <div>
                <label className="mb-2 block text-sm font-medium">
                  Detected Fields (click to add as filter)
                </label>
                <div className="flex flex-wrap gap-2">
                  {result.sample_fields.map((field) => (
                    <button
                      key={field}
                      type="button"
                      onClick={() => handleAddAsFilter(field)}
                      className="bg-muted hover:bg-primary hover:text-primary-foreground rounded-md border px-3 py-1 text-sm transition-colors"
                    >
                      <Plus size={12} className="mr-1 inline" />
                      {field}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {result?.extracted_array && result.extracted_array.length > 0 && (
              <div>
                <label className="mb-2 block text-sm font-medium">
                  Extracted Array ({result.extracted_array.length} items)
                </label>
                <div className="max-h-60 overflow-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        {result.sample_fields.map((field) => (
                          <th key={field} className="px-3 py-2 text-left font-medium">
                            {field}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.extracted_array.slice(0, 5).map((item, i) => (
                        <tr key={i} className="border-t">
                          {result.sample_fields.map((field) => (
                            <td key={field} className="max-w-48 truncate px-3 py-2">
                              {String((item as Record<string, unknown>)?.[field] ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result?.raw_response != null && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowRawResponse(!showRawResponse)}
                  className="text-muted-foreground hover:text-foreground mb-2 flex items-center gap-1 text-sm font-medium"
                >
                  {showRawResponse ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  Raw Response
                </button>
                {showRawResponse && (
                  <div className="bg-muted max-h-60 overflow-auto rounded-md border p-3">
                    <pre className="text-xs">
                      <code>{JSON.stringify(result.raw_response, null, 2)}</code>
                    </pre>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
